import React from 'react';
import axios from 'axios';
import { BASELINE } from '../util';

/** (1) 기본 데이터 세팅 */
const linear_graph_data = {
    slope: 1,
    intercept: 100,
    xMin: 0,
    xMax: 100,
    step: 10,
    options: {
        width: 400,
        height: 300,
        margin: 40,
        axisColor: "#000",
        axisWidth: 2,
        lineColor: "#000",
        lineWidth: 3,
        showGrid: true,
        gridColor: "#ccc", // 옅은 회색 그리드 (실선)
        gridWidth: 1,
        showAltText: false,
        showAxisLabels: true,
        showAxisTitles: true
    },
    distractors: [
        { slope: 2, intercept: 10 },
        { slope: -1, intercept: 0 },
        { slope: 0.5, intercept: 100 }
    ]
};

/**
 * 2) 메인 그래프 컴포넌트
 *    - y축 범위: 무조건 0~300 고정
 *    - intercept: 0~100 (10단위)로 들어오도록 설정 (아래 프롬프트에서)
 *    - top, right 프레임선은 제거 (rect 삭제), x=0·y=0만 보이게
 *    - Y 라벨은 +90도 회전
 */
const Set_linear_graph = ({ graphData }) => {
    const {
        slope,
        intercept,
        xMin,
        xMax,
        step,
        options = {}
    } = graphData || linear_graph_data;

    const {
        width = 400,
        height = 300,
        margin = 40,
        axisColor = "#000",
        axisWidth = 2,
        lineColor = "#000",
        lineWidth = 3,
        showGrid = true,
        gridWidth = 1,
        showAltText = false,
        showAxisLabels = true,
        showAxisTitles = true
    } = options;

    // (A) X, Y 값 계산
    const xValues = [];
    for (let x = xMin; x <= xMax; x += step) {
        xValues.push(x);
    }
    // 그래프 데이터(y), 실제로 300 이상이 되더라도 y=300 초과분은 넘어갈 수 있음
    const yValues = xValues.map(x => slope * x + intercept);

    // (B) y축은 무조건 0~300
    const yMinVal = 0;
    const yMaxVal = 300;

    // (C) 실제 차트 영역 크기
    const chartWidth = width - margin * 2;
    const chartHeight = height - margin * 2;

    // (D) 스케일 함수
    const scaleX = (x) =>
        margin + ((x - xMin) / (xMax - xMin)) * chartWidth;

    const scaleY = (y) =>
        margin + ((yMaxVal - y) / (yMaxVal - yMinVal)) * chartHeight;

    // (E) path 데이터 (선 그래프)
    const pathData = xValues.map((x, i) => {
        const px = scaleX(x);
        const py = scaleY(yValues[i]);
        return (i === 0) ? `M${px},${py}` : `L${px},${py}`;
    }).join(' ');

    // (F) 접근성용 텍스트
    const altText = `
    A line graph showing f(x) = ${slope}x + ${intercept}, 
    with x in [${xMin}..${xMax}] and y in [0..300].
  `;

    // (G) 그리드 표시용 x, y 눈금
    //     여기서는 간단히 일정 간격으로 나눔
    const xGridVals = [];
    const xGridStep = Math.max(1, Math.round((xMax - xMin) / 10));
    for (let xv = xMin; xv <= xMax; xv += xGridStep) {
        xGridVals.push(xv);
    }

    const yGridVals = [];
    const yGridStep = 50; // 예: 50 간격 (0,50,100,150,200,250,300)
    for (let yv = yMinVal; yv <= yMaxVal; yv += yGridStep) {
        yGridVals.push(yv);
    }

    return (
        <div>
            <svg
                width={width}
                height={height}
                xmlns="http://www.w3.org/2000/svg"
                aria-labelledby="graphDescription"
                role="img"
                style={{ backgroundColor: "#fff" }}
            >
                <title id="graphDescription">Linear Function Graph</title>
                <desc>{altText}</desc>

                {/* (1) top, right 프레임선 없음 → rect 제거 */}

                {/* (2) 그리드 라인 (실선, 회색) */}
                {showGrid && (
                    <g>
                        {xGridVals.map(xv => {
                            const xPos = scaleX(xv);
                            return (
                                <line
                                    key={`gx-${xv}`}
                                    x1={xPos} y1={scaleY(yMinVal)}
                                    x2={xPos} y2={scaleY(yMaxVal)}
                                    stroke="#ccc"
                                    strokeWidth={gridWidth}
                                />
                            );
                        })}
                        {yGridVals.map(yv => {
                            const yPos = scaleY(yv);
                            return (
                                <line
                                    key={`gy-${yv}`}
                                    x1={scaleX(xMin)} y1={yPos}
                                    x2={scaleX(xMax)} y2={yPos}
                                    stroke="#ccc"
                                    strokeWidth={gridWidth}
                                />
                            );
                        })}
                    </g>
                )}

                {/* (3) 축 (x=0, y=0)만 표시 → 왼쪽, 아래쪽 라인 */}
                {/* x=0 세로축 */}
                <line
                    x1={scaleX(0)} y1={scaleY(yMinVal)}
                    x2={scaleX(0)} y2={scaleY(yMaxVal)}
                    stroke={axisColor}
                    strokeWidth={axisWidth}
                />
                {/* y=0 가로축 */}
                <line
                    x1={scaleX(xMin)} y1={scaleY(0)}
                    x2={scaleX(xMax)} y2={scaleY(0)}
                    stroke={axisColor}
                    strokeWidth={axisWidth}
                />

                {/* (4) 축 라벨(숫자) */}
                {showAxisLabels && (
                    <g>
                        {/* X축 라벨 */}
                        {xGridVals.map(xv => (
                            <text
                                key={`xl-${xv}`}
                                x={scaleX(xv)}
                                y={scaleY(0) + 15}
                                fill="#333"
                                fontSize={13}
                                textAnchor="middle"
                            >
                                {xv}
                            </text>
                        ))}
                        {/* Y축 라벨 */}
                        {yGridVals.map(yv => (
                            <text
                                key={`yl-${yv}`}
                                x={scaleX(0) - 8}
                                y={scaleY(yv)}
                                fill="#333"
                                fontSize={13}
                                textAnchor="end"
                                alignmentBaseline="middle"
                            >
                                {yv}
                            </text>
                        ))}
                    </g>
                )}

                {/* (5) 축 이름 (X, Y) 
            - Y 레이블을 +90도(오른쪽 방향) 회전해서 보여줌 */}
                {showAxisTitles && (
                    <g>
                        {/* X 축 이름 */}
                        <text
                            x={scaleX(xMax) + 20}
                            y={scaleY(0)}
                            fontSize={14}
                            textAnchor="start"
                            alignmentBaseline="middle"
                            fill="#000"
                        >
                            X
                        </text>

                        <text
                            x={scaleX(0)}
                            y={scaleY(yMaxVal) - 10}
                            fontSize={14}
                            textAnchor="end"
                            fill="#000"
                        >
                            Y
                        </text>
                    </g>
                )}

                {/* (6) 그래프 선 (path) */}
                <path
                    d={pathData}
                    fill="none"
                    stroke={lineColor}
                    strokeWidth={lineWidth}
                />
            </svg>

            {showAltText && (
                <div style={{ marginTop: '10px', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                    {altText}
                </div>
            )}
        </div>
    );
};

/** 
 * 3) AI JSON 받아오는 부분(변경 없음)
 */
const getPerfectJSON = async (data, maxAttempts = 5) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const res = await axios.post(`${BASELINE}askai3`, data);
            if (res.data && res.data.message) {
                try {
                    const ai_response = JSON.parse(res.data.message);
                    return ai_response; // 정상적으로 JSON 파싱되면 반환
                } catch (error) {
                    console.warn(`Attempt ${attempt}: JSON parsing error, retrying...`);
                }
            } else {
                console.warn(`Attempt ${attempt}: Unexpected response structure, retrying...`);
            }
        } catch (error) {
            console.warn(`Attempt ${attempt}: axios request error (${error.message}), retrying...`);
        }
    }
    return null;
};

/** 
 * 4) 난이도별 랜덤 JSON 생성 함수 
 *    - intercept: 0~100 (10단위)
 *    - y축은 0~300 고정
 */
const LinearFunctionGraph_prompt = async (count, difficulty = "E") => {
    try {
        let difficultyText = "easy";
        if (difficulty === "M") difficultyText = "medium";
        if (difficulty === "H") difficultyText = "hard";

        const graphPromises = [];

        for (let i = 0; i < count; i++) {
            const data = {
                prompt: `
Generate a random JSON object for a linear graph, at ${difficultyText} difficulty level.

Constraints:
- xMin=0, xMax=100, step=10.
- slope can be any integer or float >= 0.
- intercept in [0, 100] in increments of 10.
- Y-axis is always 0..300 (chart).
- No negative values on the chart.

Schema:
{
  "slope": number,
  "intercept": number,
  "xMin": number,
  "xMax": number,
  "step": number,
  "options": {
    "width": 400,
    "height": 300,
    "margin": 40,
    "axisColor": "#000",
    "axisWidth": 2,
    "lineColor": "#000",
    "lineWidth": 2,
    "showGrid": true,
    "gridColor": "#000",
    "gridWidth": 1,
    "showAltText": false,
    "showAxisLabels": true,
    "showAxisTitles": true
  },
  "distractors": [
    { "slope": number, "intercept": number },
    { "slope": number, "intercept": number },
    { "slope": number, "intercept": number }
  ]
}

- Output only the JSON object, nothing else.
`
            };

            graphPromises.push(
                getPerfectJSON(data).then((ai_response) => {
                    if (ai_response) {
                        const component = Set_linear_graph({ graphData: ai_response });
                        return {
                            component,
                            data: ai_response
                        };
                    } else {
                        return null;
                    }
                })
            );
        }

        const results = await Promise.all(graphPromises);
        return results;

    } catch (error) {
        console.error("Error in LinearFunctionGraph_prompt:", error);
        return null;
    }
};

export {
    LinearFunctionGraph_prompt,
    Set_linear_graph
};
